import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { EmailSessionModel } from '@/api/models/emailSession';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GET_EMAIL_THREAD_QUERY_KEY } from './useGetEmailThread';

export type ApiResponse_RespondTo_Email = EmailSessionModel;

export type ApiRequest_RespondTo_Email = {
  sessionId: string;
  subject: string;
  response: string;
};

const METHOD = 'POST';
const ENDPOINT = 'email/send-response';

export const useRespondToEmail = (stateFns?: MutationStateFns<ApiResponse_RespondTo_Email>) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApiRequest_RespondTo_Email) =>
      handleApiCall<ApiResponse_RespondTo_Email, ApiRequest_RespondTo_Email>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {}),
        data
      ),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: GET_EMAIL_THREAD_QUERY_KEY(data.session_id),
        refetchType: 'all',
      });
      enqueueSnackbar('Email sent successfully', { variant: 'success' });

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar('Failed to sent email', { variant: 'error' });

      // eslint-disable-next-line no-console
      console.error(err);
      stateFns?.onError?.(err);
    },
  });
};
